import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { PendingUserInput, PendingToolResult } from "@/utils/ai/chatWithAI";

interface UserAnswer {
  questionId: string;
  answer: string | string[];
}

interface PendingQuestionsContextValue {
  pendingQuestions: PendingUserInput | null;
  setPendingQuestions: (questions: PendingUserInput | null) => void;
  isSubmittingAnswers: boolean;
  submitAnswers: (
    toolCallId: string,
    answers: UserAnswer[],
    skipped: boolean,
  ) => void;
  onSubmitAnswers: ((result: PendingToolResult) => Promise<void>) | null;
  setOnSubmitAnswers: (
    callback: ((result: PendingToolResult) => Promise<void>) | null,
  ) => void;
}

const PendingQuestionsContext =
  createContext<PendingQuestionsContextValue | null>(null);

export const usePendingQuestions = (): PendingQuestionsContextValue => {
  const context = useContext(PendingQuestionsContext);
  if (!context) {
    return {
      pendingQuestions: null,
      setPendingQuestions: () => {},
      isSubmittingAnswers: false,
      submitAnswers: () => {},
      onSubmitAnswers: null,
      setOnSubmitAnswers: () => {},
    };
  }
  return context;
};

export const PendingQuestionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [pendingQuestions, setPendingQuestions] =
    useState<PendingUserInput | null>(null);
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  const [onSubmitAnswers, setOnSubmitAnswersState] = useState<
    ((result: PendingToolResult) => Promise<void>) | null
  >(null);

  const setOnSubmitAnswers = useCallback(
    (callback: ((result: PendingToolResult) => Promise<void>) | null) => {
      setOnSubmitAnswersState(() => callback);
    },
    [],
  );

  const submitAnswers = useCallback(
    async (toolCallId: string, answers: UserAnswer[], skipped: boolean) => {
      if (!onSubmitAnswers || !pendingQuestions) return;

      const toolResult: PendingToolResult = {
        toolCallId,
        toolName: pendingQuestions.toolName,
        result: { answers, skipped },
      };

      setIsSubmittingAnswers(true);
      try {
        await onSubmitAnswers(toolResult);
        // Don't clear pending questions here - continuation callback handles it
      } finally {
        setIsSubmittingAnswers(false);
      }
    },
    [onSubmitAnswers, pendingQuestions],
  );

  const value = useMemo(
    () => ({
      pendingQuestions,
      setPendingQuestions,
      isSubmittingAnswers,
      submitAnswers,
      onSubmitAnswers,
      setOnSubmitAnswers,
    }),
    [
      pendingQuestions,
      isSubmittingAnswers,
      submitAnswers,
      onSubmitAnswers,
      setOnSubmitAnswers,
    ],
  );

  return (
    <PendingQuestionsContext.Provider value={value}>
      {children}
    </PendingQuestionsContext.Provider>
  );
};
